import { useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, Play, Loader2 } from 'lucide-react';
import { stepTest } from '../lib/aquaNet';

const n1 = (v, d = 2) => (v == null || !Number.isFinite(v) ? '–' : v.toFixed(d).replace('.', ','));

// step test / zone inflow: close valves in sequence, compare simulated vs. measured inflow drop
export function AquaStep({ t, project, cfg, setCfg }) {
  const [out, setOut] = useState(null);
  const [busy, setBusy] = useState(false);
  const steps = cfg.steps || [];
  const valves = (project.fittings || []).filter((f) => f.type === 'valve');
  const nm = (id) => { const f = valves.find((x) => x.id === id); return f?.name || `${t.fit_valve} ${String(id).slice(-4)}`; };
  const upd = (i, p) => setCfg({ steps: steps.map((s, k) => (k === i ? { ...s, ...p } : s)) });
  const run = async () => {
    setBusy(true);
    try { const r = await stepTest(project, cfg, steps); if (r.error) toast.error(t['aqErr_' + r.error] || t.aqErr_calc); else setOut(r.steps); } catch (e) { toast.error(String(e?.message || e)); }
    setBusy(false);
  };
  const tol = +cfg.stepTol || 0.2;
  return (
    <>
      <p className="muted">{t.aqStepHint}</p>
      <p className="aq-disc">{t.aqStepDisc}</p>
      <label className="aq-inline">{t.aqMeas0}<input type="number" step="0.01" data-testid="aq-meas0" value={cfg.meas0 ?? ''} onChange={(e) => setCfg({ meas0: e.target.value })} /> l/s</label>
      {steps.map((s, i) => (
        <div key={i} className="aq-row" data-testid={`aq-step-${i}`}>
          <b>{i + 1}.</b>
          <select data-testid={`aq-step-valve-${i}`} value={s.vid || ''} onChange={(e) => upd(i, { vid: e.target.value })}><option value="">–</option>{valves.map((f) => <option key={f.id} value={f.id}>{nm(f.id)}</option>)}</select>
          <input type="number" step="0.01" placeholder={t.aqMeas} data-testid={`aq-step-meas-${i}`} value={s.meas ?? ''} onChange={(e) => upd(i, { meas: e.target.value })} />
          <button className="icon-btn" data-testid={`aq-step-del-${i}`} onClick={() => setCfg({ steps: steps.filter((_, k) => k !== i) })}><Trash2 size={16} /></button>
        </div>
      ))}
      <div className="aq-row">
        <button className="btn btn-outline" data-testid="aq-step-add" disabled={!valves.length} onClick={() => setCfg({ steps: [...steps, { vid: '', meas: '' }] })}><Plus size={16} /> {t.aqStepAdd}</button>
        <label className="aq-inline">{t.aqTol}<input type="number" step="0.05" data-testid="aq-step-tol" value={cfg.stepTol ?? 0.2} onChange={(e) => setCfg({ stepTol: e.target.value })} /> l/s</label>
      </div>
      <button className="btn btn-accent aq-run" data-testid="aq-step-run" disabled={busy || !steps.length || steps.some((s) => !s.vid)} onClick={run}>{busy ? <Loader2 size={16} className="spin" /> : <Play size={16} />} {t.aqStepRun}</button>
      {out && (
        <div className="aq-table-wrap"><table className="aq-table" data-testid="aq-step-table">
          <thead><tr><th>#</th><th>{t.fit_valve}</th><th>{t.aqSim}</th><th>Δ {t.aqSim}</th><th>{t.aqMeas}</th><th>Δ {t.aqMeas}</th><th>{t.aqLeakQ}</th></tr></thead>
          <tbody>
            {out.map((s) => (
              <tr key={s.k} className={s.leak != null && s.leak > tol ? 'aq-leak' : ''} data-testid={`aq-step-row-${s.k}`}>
                <td>{s.k}</td><td>{s.k ? nm(s.vid) : t.aqStart}</td><td>{n1(s.sim)}</td><td>{n1(s.dSim)}</td><td>{n1(s.meas)}</td><td>{n1(s.dMeas)}</td>
                <td>{s.leak != null ? `${n1(s.leak)}${s.leak > tol ? ' ⚠️' : ''}` : '–'}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}
      {out && out.some((s) => s.leak != null && s.leak > tol) && <p className="aq-warn" data-testid="aq-step-leak">{t.aqStepLeak}</p>}
    </>
  );
}
